import { useEffect, useMemo, useRef, useState } from "react";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import { FormBuilder } from "@/components/features/FormBuilder/components/FormBuilder/FormBuilder";
import { formBuilderEndpoints } from "@/api/form-builder/formBuilder.endpoints";
import { FormBuilderQueryKeys } from "@/api/form-builder/formBuilder.queries";
import type { FormBuilderDetailData } from "@/api/form-builder/formBuilder.types";
import { useGetRequestByParam } from "@/hooks/reusable-api-hooks/useGetRequestByParam";
import { usePutRequestByParam } from "@/hooks/reusable-api-hooks/usePutRequestByParam";
import { useFormStore } from "@/store/form-builder";
import { useEntityTenantStore } from "@/store/entityTenantStore";
import { getFallbackDefaultEligibility } from "@/components/features/FormBuilder/utils/fallbackDefaultEligibility";
import { useMarkFallbackDefault } from "@/hooks/form-builder/useMarkFallbackDefault";
import { useFormVersion } from "@/hooks/form-builder/useFormVersions";
import type {
  ReadOnlyReason,
  SaveStatus,
} from "@/components/features/FormBuilder/components/Shared/Header";
import { hasFormContent } from "@/components/features/FormBuilder/utils/hasFormContent";
import { useIsServEngineSuperAdmin } from "@/hooks/form-builder/useIsServEngineSuperAdmin";
import {
  buildAssignmentPayloads,
  scopeAssignmentsSchema,
} from "@/components/features/FormBuilder/utils/scopeAssignments";
import { useCreateAssignments } from "@/hooks/form-builder/useFormBuilderAssignments";
import {
  createAssignment,
  deleteAssignment,
} from "@/api/form-builder-assignments/formBuilderAssignments.mutations";
import {
  replaceSlotRow,
  SlotLeftEmptyError,
} from "@/components/features/FormBuilder/utils/replaceSlotRow";
import { FormBuilderAssignmentsQueryKeys } from "@/api/form-builder-assignments/formBuilderAssignments.queries";
import {
  assignToSchema,
  toAssignmentPayload,
  toSlotPageSearch,
  type AssignTo,
} from "@/components/features/FormBuilder/utils/assignTo";
import {
  findSlotRow,
  proposeSlotChange,
  type AssignmentSlot,
} from "@/components/features/FormBuilder/utils/assignHandoffSlot";
import { notifyReplacedSlots } from "@/components/features/FormBuilder/utils/replacedSlots";
import {
  notifyFormPublished,
  resolvePublishedFormAssignment,
} from "@/components/features/FormBuilder/utils/publishedFormAssignment";
import { getAssignmentsTree } from "@/api/form-builder-assignments/formBuilderAssignments.queries";
import {
  useAssistantHandoffStore,
  type AssignHandoff,
  type ReturnHandoff,
} from "@/store/form-builder/useAssistantHandoffStore";
import { useAssignmentsAssistantStore } from "@/store/form-builder/useAssignmentsAssistantStore";
import { editorVisitKey } from "@/store/form-builder/tabStorage";
import { formNameTakenMessage, isFormNameTaken } from "@/api/form-builder/formNameTaken";
import { compressIfNeeded } from "@/utils/compressPayload";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import styles from "../form-builder.module.scss";
import Loader from "@/components/ui/Loader/Loader";
import { Alert, Button } from "@onengine/component-library";
import {
  handleApiError,
  handleApiSuccess,
  showAlert,
  showErrorAlert,
  showInfoAlert,
  showWarningAlert,
} from "@/utils/responseHandler";
import type { ApiError } from "@/types/common.types";
import type {
  FormBuilderVisibility,
  SaveFormResponseData,
} from "@/api/form-builder/formBuilder.types";

const AUTOSAVE_ERROR_MESSAGE =
  "Your latest changes weren't saved. We'll try again on your next edit.";
const FORM_VISIBILITY_IN_USE_MESSAGE =
  "This form is already in use by one or more entities and can no longer be marked global.";

const ASSIGNMENTS_NOT_CREATED_MESSAGE =
  "The form was published, but its assignments could not be created. Review them on the Assignments screen.";
const ASSIGNMENTS_UNMAPPED_MESSAGE =
  "The form was published, but its Form Type or Work Order Type has no assignment slot. Assign it on the Assignments screen.";

const ASSIGN_FAILED_FALLBACK = "the assignment was refused";

const AUTO_SAVE_DEBOUNCE_MS = 400;

// The editor has no rename control, so the message says where the name can change. A form with
// no id was never stored, hence no row to rename in the list.
const nameTakenSaveError = (name: string, hasSavedForm: boolean) =>
  `${formNameTakenMessage(name)} ${
    hasSavedForm
      ? 'Rename it from "Edit form details" in the form list.'
      : "Go back and create the form again with another name."
  }`;
const PUBLISH_ERROR_MESSAGE = "Failed to publish the form. Please try again.";
const NO_CHANGES_MESSAGE = "This content is already the published version. Nothing new to publish.";

async function getStructureForSave(compactJson?: string) {
  const currentJSON = compactJson ?? useFormStore.getState().getCompactJSON();
  const { value, isCompressed } = await compressIfNeeded(currentJSON);
  return {
    structure: isCompressed ? value : useFormStore.getState().pages,
    isCompressed,
  };
}

// Session storage can be blocked (private mode, site data off); the form then
// opens as it did before.
function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Not kept for a reload; nothing else depends on it.
  }
}

function removeSession(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing was kept.
  }
}

function getFormIdFromSaveResponse(data: unknown): string | undefined {
  if (data == null || typeof data !== "object") return undefined;
  const obj = data as { id?: unknown; formId?: unknown };
  const candidate = obj.id ?? obj.formId;
  return typeof candidate === "string" && candidate.length > 0 ? candidate : undefined;
}

function parseIdCsv(value: string | undefined): number[] {
  if (!value) return [];
  return value
    .split(",")
    .map((part) => Number(part.trim()))
    .filter((n) => Number.isFinite(n));
}

function parseSingleId(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function getReadOnlyReason(isVersionView: boolean, status: string | undefined): ReadOnlyReason {
  if (isVersionView) return "version";
  return status === "ARCHIVED" ? "archived" : "global";
}

const editorSearchSchema = z.object({
  formId: z.string().optional(),
  tenantId: z.string().optional(),
  visibility: z.enum(["TENANT", "ALL_TENANTS"]).optional(),
  formName: z.string().optional(),
  formDescription: z.string().optional(),
  formTypeId: z.string().optional(),
  workOrderTypeId: z.string().optional(),
  tradeIds: z.string().optional(),
  subTradeIds: z.string().optional(),
  levelIds: z.string().optional(),
  scopeAssignments: scopeAssignmentsSchema.optional(),
  assignTo: assignToSchema.optional().catch(undefined),
  version: z.coerce.number().int().positive().optional().catch(undefined),
  /** Templates list search the editor was opened from, so closing it returns to the same filters. */
  returnSearch: z.record(z.string(), z.unknown()).optional().catch(undefined),
});

export const Route = createFileRoute("/form-builder/editor/")({
  component: FormBuilderEditorPage,
  validateSearch: editorSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

export function FormBuilderEditorPage() {
  const navigate = useNavigate();
  const entityTenant = useEntityTenantStore((state) => state.entityTenant);
  const isServEngineSuperAdmin = useIsServEngineSuperAdmin();
  const { mutate: markFallbackDefault } = useMarkFallbackDefault();
  const { mutateAsync: createAssignments } = useCreateAssignments();
  const queryClient = useQueryClient();
  const scopeAssignmentsCreatedRef = useRef(false);
  const {
    formId: formIdFromSearch,
    tenantId: tenantIdFromSearch,
    visibility: visibilityFromSearch,
    formName: initialFormName,
    formDescription,
    formTypeId: formTypeIdParam,
    workOrderTypeId: workOrderTypeIdParam,
    tradeIds: tradeIdsParam,
    subTradeIds: subTradeIdsParam,
    levelIds: levelIdsParam,
    scopeAssignments,
    assignTo: assignToFromSearch,
    version: viewingVersion,
    returnSearch,
  } = useSearch({
    from: "/form-builder/editor/",
  });

  // Held in state so the header note goes away once the one assignment attempt has been made.
  const [assignTo, setAssignTo] = useState<AssignTo | undefined>(assignToFromSearch);
  // Spent in the publish callback itself, before anything async, so a second click cannot assign again.
  const assignToRef = useRef<AssignTo | undefined>(assignToFromSearch);
  const [isAssigning, setIsAssigning] = useState(false);

  // Entity picked in the label menu wins; a ServEngine-scope super admin editing
  // a form from the cross-entity list falls back to the tenant the row carried.
  const effectiveTenant = entityTenant || tenantIdFromSearch || undefined;

  const metadataFromSearch = useMemo(() => {
    const fields: Record<string, unknown> = {};
    const isFromModal = formTypeIdParam !== undefined;
    if (!isFromModal) {
      if (formDescription) fields.description = formDescription;
      return fields;
    }

    const formTypeId = parseSingleId(formTypeIdParam);
    const workOrderTypeId = parseSingleId(workOrderTypeIdParam);

    if (formTypeId !== undefined) fields.formTypeId = formTypeId;
    if (workOrderTypeId !== undefined) fields.workOrderTypeId = workOrderTypeId;
    fields.tradeIds = parseIdCsv(tradeIdsParam);
    fields.subTradeIds = parseIdCsv(subTradeIdsParam);
    fields.levelIds = parseIdCsv(levelIdsParam);
    if (formDescription) fields.description = formDescription;
    return fields;
  }, [
    formTypeIdParam,
    workOrderTypeIdParam,
    tradeIdsParam,
    subTradeIdsParam,
    levelIdsParam,
    formDescription,
  ]);
  const initFromJSON = useFormStore((s) => s.initFromJSON);

  // SERV-3262: a new form keeps the URL it opened with; its first save's id is kept
  // for this visit, so a reload opens that draft instead of a blank form that would
  // save a second copy. Opening the same link again is a new visit and a new form.
  const [newDraftKey] = useState(() => `form-builder:new-draft:${editorVisitKey()}`);
  const [restoredDraftId, setRestoredDraftId] = useState(() =>
    formIdFromSearch ? undefined : (readSession(newDraftKey) ?? undefined),
  );
  const loadedFormId = formIdFromSearch ?? restoredDraftId;

  /** After first save, API returns formId — use for subsequent PUTs when URL has no formId */
  const [draftFormId, setDraftFormId] = useState<string | undefined>(restoredDraftId);
  const draftFormIdRef = useRef<string | undefined>(restoredDraftId);
  const effectiveFormId = formIdFromSearch ?? draftFormId;

  const [isPublishingManual, setIsPublishingManual] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ state: "idle" });
  // The name the server refused. Autosave stays quiet for it until the name changes.
  const rejectedNameRef = useRef<string | null>(null);
  // Keyed by message so a failure streak toasts once, while a different error still gets its own toast.
  const toastedAutoSaveErrorRef = useRef<string | null>(null);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  // The server's word on whether the saved draft differs from the published version, and
  // whether the editor has changed the form since it last heard from the server.
  const [serverUnpublished, setServerUnpublished] = useState(false);
  const [editedSincePublish, setEditedSincePublish] = useState(false);

  const goToList = () => navigate({ to: "/form-builder/templates", search: returnSearch as never });

  const isVersionView = !!formIdFromSearch && viewingVersion !== undefined;

  const {
    data: draftDetail,
    isLoading: isDraftLoading,
    error: draftError,
  } = useGetRequestByParam<FormBuilderDetailData>({
    path: formBuilderEndpoints.forms,
    key: FormBuilderQueryKeys.detail,
    param: loadedFormId
      ? `${loadedFormId}${effectiveTenant ? `?tenantId=${encodeURIComponent(effectiveTenant)}` : ""}`
      : undefined,
    errorTag: "formBuilderDetail",
    enabled: !!loadedFormId && !isVersionView,
  });

  // A kept draft that no longer opens (deleted, another company) is dropped, so the
  // form starts new instead of saving into an id that is gone.
  useEffect(() => {
    if (!draftError || !restoredDraftId) return;
    removeSession(newDraftKey);
    draftFormIdRef.current = undefined;
    setDraftFormId(undefined);
    setRestoredDraftId(undefined);
  }, [draftError, restoredDraftId, newDraftKey]);

  const {
    data: versionDetail,
    isLoading: isVersionLoading,
    isError: isVersionError,
  } = useFormVersion(
    isVersionView ? formIdFromSearch : undefined,
    viewingVersion,
    tenantIdFromSearch,
  );

  const formDetail = useMemo<FormBuilderDetailData | undefined>(
    () => (isVersionView ? versionDetail && { ...versionDetail, readOnly: true } : draftDetail),
    [isVersionView, versionDetail, draftDetail],
  );
  const isLoading = isVersionView ? isVersionLoading : isDraftLoading;

  const { mutate: saveForm, isLoading: isSaving } = usePutRequestByParam({
    queryKey: FormBuilderQueryKeys.list,
  });

  // Hydrate the builder once per loaded form or version: a refetch of the same form (e.g. after
  // marking it the fallback default) must not overwrite edits the autosave has not sent yet.
  const hydratedFormRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!formDetail?.structure) return;
    const loadedForm = `${loadedFormId}:${viewingVersion ?? "draft"}`;
    if (hydratedFormRef.current === loadedForm) return;
    hydratedFormRef.current = loadedForm;
    initFromJSON(formDetail.structure);
    const json = useFormStore.getState().getCompactJSON();
    lastSavedRef.current = json;
    if (formDetail.status === "PUBLISHED") {
      lastPublishedRef.current = json;
    }
  }, [formDetail, initFromJSON, loadedFormId, viewingVersion]);

  const resolvedFormName = formDetail?.name || initialFormName || "Untitled Form";

  useEffect(() => {
    setServerUnpublished(!!formDetail?.hasUnpublishedChanges);
  }, [formDetail?.hasUnpublishedChanges]);

  useEffect(() => {
    if (rejectedNameRef.current === null || rejectedNameRef.current === resolvedFormName) return;
    rejectedNameRef.current = null;
    setSaveError(undefined);
    setSaveStatus({ state: "idle" });
    // Nothing else is queued: the edits the refusal held back would wait for the next keystroke.
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(executeSave, AUTO_SAVE_DEBOUNCE_MS);
  }, [resolvedFormName]);

  const metadataFromDetail = useMemo(() => {
    if (!formDetail) return null;
    const fields: Record<string, unknown> = {};
    if (formDetail.formType?.id !== undefined) {
      fields.formTypeId = formDetail.formType.id;
    }
    if (formDetail.workOrderType?.id !== undefined) {
      fields.workOrderTypeId = formDetail.workOrderType.id;
    }
    fields.tradeIds = (formDetail.trades ?? []).map((trade) => trade.id);
    fields.subTradeIds = (formDetail.subTrades ?? []).map((trade) => trade.id);
    fields.levelIds = (formDetail.levels ?? []).map((level) => level.id);
    if (formDetail.description) fields.description = formDetail.description;
    return fields;
  }, [formDetail]);

  const metadataForSave = metadataFromDetail ?? metadataFromSearch;
  const metadataRef = useRef(metadataForSave);
  metadataRef.current = metadataForSave;

  // A global form is only editable by the ServEngine-scope super admin; everyone
  // else (including a super admin who has since dropped into an entity) gets it
  // read-only, per the detail response's own `readOnly` flag.
  const isReadOnly = isVersionView || !!formDetail?.readOnly;
  // Read through a ref so a save retried from a stale onSettled closure still sees a
  // version view opened since, instead of saving that version's structure as the draft.
  const isReadOnlyRef = useRef(isReadOnly);
  isReadOnlyRef.current = isReadOnly;
  const canFlagVisibility =
    isServEngineSuperAdmin && (formDetail?.origin ?? "platform") === "platform";

  const fallbackEligibility = getFallbackDefaultEligibility({
    visibility: formDetail?.visibility,
    status: formDetail?.status,
    formTypeLabel: formDetail?.formType?.label,
    isFallbackDefault: !!formDetail?.isFallbackDefault,
  });
  const [visibility, setVisibility] = useState<FormBuilderVisibility | undefined>(
    visibilityFromSearch,
  );
  const lastSavedVisibilityRef = useRef<FormBuilderVisibility | undefined>(visibilityFromSearch);
  useEffect(() => {
    if (formDetail) {
      setVisibility(formDetail.visibility);
      lastSavedVisibilityRef.current = formDetail.visibility;
    }
  }, [formDetail]);

  // Auto-save: debounced after last edit
  const pages = useFormStore((s) => s.pages);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastSavedRef = useRef<string>("");
  const lastPublishedRef = useRef<string>("");
  const isMountedRef = useRef(false);
  const autoSaveInFlightRef = useRef(false);
  const pendingAutoSaveRef = useRef(false);

  // When switching between forms, cancel any pending save from the previous form
  // so its onSettled callback doesn't trigger a save against the new form's ID.
  useEffect(() => {
    clearTimeout(autoSaveTimerRef.current);
    pendingAutoSaveRef.current = false;
    lastSavedRef.current = useFormStore.getState().getCompactJSON();
  }, [formIdFromSearch]);
  const resolvedFormNameRef = useRef(resolvedFormName);
  resolvedFormNameRef.current = resolvedFormName;

  const applyDraftFormIdFromResponse = (data: unknown) => {
    if (formIdFromSearch) return;
    const newId = getFormIdFromSaveResponse(data);
    if (newId) {
      draftFormIdRef.current = newId;
      setDraftFormId(newId);
      writeSession(newDraftKey, newId);
    }
  };

  const executeSave = async () => {
    if (isReadOnlyRef.current) return;
    const currentJSON = useFormStore.getState().getCompactJSON();
    const visibilityForPayload = canFlagVisibility ? visibility : undefined;
    const visibilityChanged = canFlagVisibility && visibility !== lastSavedVisibilityRef.current;
    if (currentJSON === lastSavedRef.current && !visibilityChanged) return;
    // The server refused this name; saving again would only be refused again.
    if (rejectedNameRef.current === resolvedFormNameRef.current) return;
    // No draft is created until the form holds a question. Leaves the saved markers alone,
    // so the first question still counts as a change.
    const hasId = !!(formIdFromSearch ?? draftFormIdRef.current);
    if (!hasId && !hasFormContent(useFormStore.getState().pages)) return;

    if (autoSaveInFlightRef.current) {
      pendingAutoSaveRef.current = true;
      return;
    }

    autoSaveInFlightRef.current = true;

    const { structure, isCompressed } = await getStructureForSave(currentJSON);
    const idForPayload = formIdFromSearch ?? draftFormIdRef.current ?? null;
    const nameForPayload = resolvedFormNameRef.current;

    setSaveStatus({ state: "saving" });
    saveForm(
      {
        path: formBuilderEndpoints.forms,
        data: {
          id: idForPayload,
          name: nameForPayload,
          structure,
          isCompressed,
          status: "DRAFT",
          ...(effectiveTenant ? { tenantId: effectiveTenant } : {}),
          ...(visibilityForPayload ? { visibility: visibilityForPayload } : {}),
          ...metadataRef.current,
        },
        errorTag: "autoSave",
        // The status line already says "Not saved"; one toast per failure streak, not one per retry.
        suppressErrorAlert: true,
      },
      {
        onSuccess: (data) => {
          applyDraftFormIdFromResponse(data);
          lastSavedRef.current = currentJSON;
          if (canFlagVisibility) lastSavedVisibilityRef.current = visibilityForPayload;
          rejectedNameRef.current = null;
          toastedAutoSaveErrorRef.current = null;
          setSaveError(undefined);
          setSaveStatus({ state: "saved", at: new Date() });
          const unpublished = (data as SaveFormResponseData | undefined)?.hasUnpublishedChanges;
          if (typeof unpublished === "boolean") {
            setServerUnpublished(unpublished);
            setEditedSincePublish(false);
          }
        },
        onError: (error) => {
          const apiError = error as ApiError;
          const code = apiError.response?.data?.error?.code;
          setSaveStatus({ state: "error" });
          if (isFormNameTaken(error)) {
            rejectedNameRef.current = nameForPayload;
            setSaveError(nameTakenSaveError(nameForPayload, !!idForPayload));
          } else if (code === "FORM_VISIBILITY_IN_USE") {
            showWarningAlert(FORM_VISIBILITY_IN_USE_MESSAGE, "Form In Use");
            if (visibilityChanged) setVisibility(lastSavedVisibilityRef.current);
          } else {
            const status = apiError.response?.status ?? 0;
            const serverMessage = apiError.response?.data?.error?.message;
            const message = status < 500 && serverMessage ? serverMessage : AUTOSAVE_ERROR_MESSAGE;
            if (toastedAutoSaveErrorRef.current !== message) {
              toastedAutoSaveErrorRef.current = message;
              showErrorAlert(message, "Changes not saved");
            }
          }
        },
        onSettled: () => {
          autoSaveInFlightRef.current = false;
          if (pendingAutoSaveRef.current) {
            pendingAutoSaveRef.current = false;
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = setTimeout(executeSave, AUTO_SAVE_DEBOUNCE_MS);
          }
        },
      },
    );
  };

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }

    if (isReadOnly) return;

    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(executeSave, AUTO_SAVE_DEBOUNCE_MS);

    return () => clearTimeout(autoSaveTimerRef.current);
  }, [pages, visibility]);

  useEffect(() => {
    if (isReadOnly || formDetail?.status !== "PUBLISHED" || !lastPublishedRef.current) return;
    if (useFormStore.getState().getCompactJSON() !== lastPublishedRef.current) {
      setEditedSincePublish(true);
    }
  }, [pages]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => clearTimeout(autoSaveTimerRef.current);
  }, []);

  const createScopeAssignments = async (templateId: string | undefined) => {
    if (!scopeAssignments || formIdFromSearch || scopeAssignmentsCreatedRef.current) return;
    if (!templateId) return;
    scopeAssignmentsCreatedRef.current = true;

    const payloads = buildAssignmentPayloads({
      scope: scopeAssignments,
      templateId,
      tradeIds: parseIdCsv(tradeIdsParam),
      subTradeIds: parseIdCsv(subTradeIdsParam),
    });
    if (!payloads) {
      showWarningAlert(ASSIGNMENTS_UNMAPPED_MESSAGE, "Assignments Not Created");
      return;
    }

    await createAssignments(payloads)
      .then(() => handleApiSuccess(`${payloads.length} assignment(s) created`, "Assignments"))
      .catch(() => showWarningAlert(ASSIGNMENTS_NOT_CREATED_MESSAGE, "Assignments Not Created"));
  };

  // SERV-3262: only the form the assignments assistant opened goes back there.
  const assignHandoffRef = useRef<AssignHandoff | null>(null);
  // A published form the assistant opened to propose a change goes back there too.
  const returnHandoffRef = useRef<ReturnHandoff | null>(null);
  useEffect(() => {
    const handoffs = useAssistantHandoffStore.getState();
    const wayBack = handoffs.wayBack(formIdFromSearch);
    assignHandoffRef.current ??= wayBack.assign;
    returnHandoffRef.current ??= wayBack.back;
    // Held while this form is open (a reload keeps it), released once it is left.
    handoffs.hold({ assign: assignHandoffRef.current, back: returnHandoffRef.current });
    return () => useAssistantHandoffStore.getState().release();
  }, []);

  const goBackAfterPublish = () => {
    const back = returnHandoffRef.current;
    if (back) navigate({ href: back.returnTo.href });
    else goToList();
  };

  // The person asked the assignments assistant for this form for one slot. Once it
  // is published they go back to that page, where the assistant asks whether to
  // apply it there: replacing the company's row in the slot, or creating one.
  const finishHandoff = (handoff: AssignHandoff, templateId: string | undefined) => {
    const assistant = useAssignmentsAssistantStore.getState();
    const formName = resolvedFormName;
    const slot: AssignmentSlot = {
      tenantId: handoff.tenantId,
      workOrderType: handoff.workOrderType,
      formType: handoff.formType,
      ...handoff.scope,
    };
    navigate({ href: handoff.returnTo.href });
    if (!templateId) {
      assistant.addError(
        `"${formName}" is published, but its id did not come back, so it cannot be assigned from here. Pick it in ${handoff.slotLabel}.`,
      );
      return;
    }
    getAssignmentsTree({ tenantId: slot.tenantId, clientId: slot.clientId, brandId: slot.brandId })
      .then((tree) => {
        const change = proposeSlotChange(tree, slot, {
          templateId,
          formName,
          slotLabel: handoff.slotLabel,
        });
        if (!change) {
          assistant.addNote(
            `"${formName}" is published and already used for ${handoff.slotLabel}.`,
          );
          return;
        }
        assistant.askToApply({
          tenantId: handoff.tenantId,
          text: `"${formName}" is published. Apply it to ${handoff.slotLabel}?`,
          change,
        });
      })
      .catch(() =>
        assistant.addError(
          `"${formName}" is published, but the assignments could not be read to assign it. Ask me to assign it, or pick it in ${handoff.slotLabel}.`,
        ),
      );
  };

  // The form was created for a slot. It is published by now, so a refusal here leaves it published
  // and says so; the attempt is spent either way, so a later publish does not retry it quietly.
  const assignPublishedToSlot = async (target: AssignTo, templateId: string | undefined) => {
    const goToSlot = () =>
      navigate({
        to: "/form-builder/assignments/$category",
        params: { category: target.category },
        search: toSlotPageSearch(target),
        replace: true,
      });
    setIsAssigning(true);
    try {
      if (!templateId) throw new Error("the form's id did not come back");
      const slot: AssignmentSlot = {
        tenantId: target.tenantId,
        workOrderType: target.category,
        formType: target.formType,
        clientId: target.clientId,
        brandId: target.brandId,
        tradeId: target.tradeId,
        subtradeId: target.subtradeId,
      };
      const tree = await getAssignmentsTree({
        tenantId: target.tenantId,
        clientId: target.clientId,
        brandId: target.brandId,
      });
      const row = findSlotRow(tree, slot);
      if (row?.templateId !== templateId) {
        try {
          await replaceSlotRow({
            row,
            create: toAssignmentPayload(target, templateId),
            deleteRow: deleteAssignment,
            createRow: createAssignment,
          });
        } finally {
          await queryClient.invalidateQueries({
            queryKey: [FormBuilderAssignmentsQueryKeys.tree],
          });
        }
      }
      handleApiSuccess(`Published and assigned to ${target.label}`, "Published");
      goToSlot();
    } catch (error) {
      const slotLeftEmpty = error instanceof SlotLeftEmptyError;
      const cause = slotLeftEmpty ? error.cause : error;
      const reason =
        (cause as ApiError).response?.data?.error?.message ??
        (cause instanceof Error ? cause.message : ASSIGN_FAILED_FALLBACK);
      const toastId = `assign-failed-${templateId ?? target.label}`;
      showAlert({
        id: toastId,
        title: "Published, but not assigned",
        message: `Published, but couldn't assign: ${reason}${
          slotLeftEmpty
            ? `. The previous form${error.previousFormName ? ` "${error.previousFormName}"` : ""} was removed from the slot and could not be restored.`
            : ""
        }`,
        type: "warning",
        autoClose: false,
        actions: [{ label: "Go to slot", onClick: goToSlot }],
      });
      // The URL becomes the published form's, so a reload or Back never lands on the unassigned draft link.
      void navigate({
        to: "/form-builder/editor",
        search: (previous) => ({
          ...previous,
          ...(templateId ? { formId: templateId } : {}),
          assignTo: undefined,
        }),
        replace: true,
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const notifyPublished = async (templateId: string | undefined) => {
    // Without an entity, a ServEngine super admin gets every tenant's tree and no
    // platform fallback data, so the check could only mislabel a global form.
    const canCheckAssignment = !(isServEngineSuperAdmin && !effectiveTenant);
    notifyFormPublished({
      formId: templateId,
      formName: resolvedFormName,
      assignment: await resolvePublishedFormAssignment(
        canCheckAssignment ? templateId : undefined,
        (signal) =>
          getAssignmentsTree(effectiveTenant ? { tenantId: effectiveTenant } : {}, signal),
      ),
      onGoToAssignments: () => navigate({ to: "/form-builder/assignments" }),
    });
  };

  const handlePublish = async () => {
    if (isReadOnly) return;
    const currentJSON = useFormStore.getState().getCompactJSON();
    const visibilityForPayload = canFlagVisibility ? visibility : undefined;
    const visibilityChanged = canFlagVisibility && visibility !== lastSavedVisibilityRef.current;

    if (
      formDetail?.status === "PUBLISHED" &&
      currentJSON === lastPublishedRef.current &&
      !serverUnpublished &&
      !visibilityChanged
    ) {
      goBackAfterPublish();
      return;
    }

    setIsPublishingManual(true);
    const lastSavedBeforePublish = lastSavedRef.current;
    lastSavedRef.current = currentJSON;
    try {
      const { structure, isCompressed } = await getStructureForSave(currentJSON);

      const idForPayload = formIdFromSearch ?? draftFormIdRef.current ?? null;

      const payload: Record<string, unknown> = {
        id: idForPayload,
        name: resolvedFormName,
        structure,
        isCompressed,
        status: "PUBLISHED",
        ...(effectiveTenant ? { tenantId: effectiveTenant } : {}),
        ...(visibilityForPayload ? { visibility: visibilityForPayload } : {}),
        ...metadataRef.current,
      };

      saveForm(
        {
          path: formBuilderEndpoints.forms,
          data: payload,
          errorTag: "formBuilderSave",
        },
        {
          onSuccess: (data) => {
            applyDraftFormIdFromResponse(data);
            removeSession(newDraftKey);
            notifyReplacedSlots(data);
            lastPublishedRef.current = currentJSON;
            setServerUnpublished(false);
            setEditedSincePublish(false);
            if (canFlagVisibility) lastSavedVisibilityRef.current = visibilityForPayload;
            const publishedFormId = getFormIdFromSaveResponse(data) ?? draftFormIdRef.current;
            const handoff = assignHandoffRef.current;
            if (handoff) {
              assignHandoffRef.current = null;
              finishHandoff(handoff, publishedFormId);
              return;
            }
            const back = returnHandoffRef.current;
            if (back) {
              returnHandoffRef.current = null;
              useAssignmentsAssistantStore
                .getState()
                .addNote(`You published the update to "${resolvedFormName}".`);
              navigate({ href: back.returnTo.href });
              return;
            }
            const slotTarget = assignToRef.current;
            assignToRef.current = undefined;
            setAssignTo(undefined);
            if (slotTarget) setIsAssigning(true);
            void createScopeAssignments(publishedFormId).then(() =>
              slotTarget
                ? assignPublishedToSlot(slotTarget, publishedFormId)
                : notifyPublished(publishedFormId),
            );
            if (!slotTarget) goToList();
          },
          onError: (error) => {
            const apiError = error as ApiError;
            if (apiError.response?.data?.error?.code === "FORM_VISIBILITY_IN_USE") {
              showWarningAlert(FORM_VISIBILITY_IN_USE_MESSAGE, "Form In Use");
              if (visibilityChanged) {
                setVisibility(lastSavedVisibilityRef.current);
              }
              return;
            }
            if (isFormNameTaken(apiError)) {
              // Stay on the form: nothing was published, and the name still has to change.
              lastSavedRef.current = lastSavedBeforePublish;
              rejectedNameRef.current = resolvedFormName;
              setSaveError(
                nameTakenSaveError(
                  resolvedFormName,
                  !!(formIdFromSearch ?? draftFormIdRef.current),
                ),
              );
              setSaveStatus({ state: "error" });
              return;
            }
            if (apiError.response?.data?.error?.code === "NO_CHANGES") {
              showInfoAlert(NO_CHANGES_MESSAGE, "No Changes");
              goBackAfterPublish();
              return;
            }
            handleApiError(apiError, PUBLISH_ERROR_MESSAGE);
          },
          onSettled: () => {
            setIsPublishingManual(false);
          },
        },
      );
    } catch {
      setIsPublishingManual(false);
      showErrorAlert(PUBLISH_ERROR_MESSAGE);
    }
  };

  // Only a form that is already published has a live version to compare the draft against.
  const publishedHasChanges =
    !isReadOnly &&
    formDetail?.status === "PUBLISHED" &&
    (serverUnpublished ||
      editedSincePublish ||
      (canFlagVisibility && visibility !== lastSavedVisibilityRef.current));

  if (isLoading && loadedFormId) {
    return (
      <section className={styles.section}>
        <div className={styles.container}>
          <Loader size="medium" />
        </div>
      </section>
    );
  }

  if (isVersionView && isVersionError) {
    return (
      <section className={styles.section}>
        <div className={styles.container}>
          <Alert
            title={`Version v${viewingVersion} couldn't be loaded`}
            message="It may not exist or you may not have access to it."
            type="danger"
          />
          <Button
            variant="secondary"
            size="small"
            onClick={() => navigate({ to: "/form-builder" })}
          >
            Back to forms
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <FormBuilder
          initialData={formDetail?.structure ?? []}
          onChange={() => {}}
          onPublish={handlePublish}
          isSaving={isSaving}
          isPublishing={isPublishingManual || isAssigning}
          isAssigning={isAssigning}
          formName={resolvedFormName}
          formId={effectiveFormId}
          tenantId={tenantIdFromSearch}
          readOnly={isReadOnly}
          readOnlyReason={getReadOnlyReason(isVersionView, formDetail?.status)}
          viewingVersion={viewingVersion}
          visibility={canFlagVisibility ? (visibility ?? "TENANT") : undefined}
          onVisibilityChange={canFlagVisibility ? setVisibility : undefined}
          editingPublishedForm={formDetail?.status === "PUBLISHED"}
          assignTo={assignTo?.label}
          saveStatus={saveStatus}
          saveError={saveError}
          hasUnpublishedChanges={publishedHasChanges}
          confirmPublish={publishedHasChanges}
          fallbackDefault={
            canFlagVisibility && formIdFromSearch
              ? {
                  ...fallbackEligibility,
                  disabledReason: fallbackEligibility.reason,
                  onActivate: () => markFallbackDefault(formIdFromSearch),
                }
              : undefined
          }
        />
      </div>
    </section>
  );
}
